import axios from 'axios';

// In production, use relative URLs (same domain)
// In development, use localhost or custom REACT_APP_API_URL
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // In production (when not in development), use relative path
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }
  // In development, use localhost
  return 'http://localhost:5001/api';
};

const API_BASE_URL = getApiBaseUrl();

export const getWhatsAppWebSocketUrl = () => {
  const token = localStorage.getItem('token') || '';
  const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
  if (process.env.REACT_APP_API_URL) {
    const base = process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '').replace(/^https?/, (m) => m === 'https' ? 'wss' : 'ws');
    return `${base}/ws/whatsapp${tokenParam}`;
  }
  if (process.env.NODE_ENV === 'production') {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/ws/whatsapp${tokenParam}`;
  }
  return `ws://localhost:5001/ws/whatsapp${tokenParam}`;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add CRM JWT to requests when present — do not overwrite an explicit Authorization
// (admin routes pass Bearer ADMIN_TOKEN; without this, CRM token replaces it and admin APIs 403).
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const h = config.headers || {};
    const already = h.Authorization ?? h.authorization;
    if (token && !already) {
      h.Authorization = `Bearer ${token}`;
      config.headers = h;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors (unauthorized) - token expired or invalid
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Admin console uses its own token; failed admin auth must not clear CRM JWT or reload
      if (window.location.pathname.startsWith('/admin')) {
        return Promise.reject(error);
      }
      // Don't reload if we're on the auth/login page - let the component handle the error
      const isAuthPage = window.location.pathname === '/' || 
                        window.location.pathname.includes('login') || 
                        window.location.pathname.includes('register') ||
                        !localStorage.getItem('token'); // If no token, we're likely on auth page
      
      if (!isAuthPage) {
        // Token expired or invalid, clear storage and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.reload();
      }
      // If on auth page, just reject the error so the component can handle it
    }
    return Promise.reject(error);
  }
);

// Clients API (unified: includes both prospects and customers)
export const getClients = (search, status, favorite, customer_type, importance) => {
  const params = {};
  if (search) params.search = search;
  if (status) params.status = status;
  if (favorite) params.favorite = favorite;
  if (customer_type) params.customer_type = customer_type;
  if (importance) params.importance = importance;
  return api.get('/clients', { params });
};

export const getClient = (id) => api.get(`/clients/${id}`);
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/clients/${id}`);
export const bulkDeleteClients = (ids) => api.post('/clients/bulk-delete', { ids });
export const bulkImportClients = (contacts) => api.post('/clients/bulk', { contacts });
export const toggleClientFavorite = (id, is_favorite) => api.put(`/clients/${id}/favorite`, { is_favorite });

// ========== EMPLOYEE MANAGEMENT API ==========
export const getEmployees = () => api.get('/employees');
export const createEmployee = (data) => api.post('/employees', data);
export const updateEmployee = (id, data) => api.put(`/employees/${id}`, data);
export const deleteEmployee = (id) => api.delete(`/employees/${id}`);

// ========== DEAL ASSIGNMENT API ==========
export const assignEmployeesToDeal = (dealId, employeeIds) => api.post(`/deals/${dealId}/assign`, { employee_ids: employeeIds });
export const getDealAssignments = (dealId) => api.get(`/deals/${dealId}/assignments`);
export const updateDealProgress = (dealId, data) => api.put(`/deals/${dealId}/progress`, data);
export const getEmployeeDeals = (employeeId) => api.get(`/employees/${employeeId}/deals`);
export const getEmployeeUnitsWithListings = (employeeId) => api.get(`/employees/${employeeId}/units-with-listings`);

// Leads API
export const getLeads = (search, importance, favorite) => {
  const params = {};
  if (search) params.search = search;
  if (importance) params.importance = importance;
  if (favorite) params.favorite = favorite;
  return api.get('/leads', { params });
};

export const getLead = (id) => api.get(`/leads/${id}`);
export const createLead = (data) => api.post('/leads', data);
export const updateLead = (id, data) => api.put(`/leads/${id}`, data);
export const deleteLead = (id) => api.delete(`/leads/${id}`);
export const bulkDeleteLeads = (ids) => api.post('/leads/bulk-delete', { ids });
export const convertLeadToClient = (id) => api.post(`/leads/${id}/convert`);
export const bulkImportLeads = (contacts) => api.post('/leads/bulk', { contacts });
export const toggleLeadFavorite = (id, is_favorite) => api.put(`/leads/${id}/favorite`, { is_favorite });

// Activities API
export const getActivities = (type, id) => api.get(`/activities/${type}/${id}`);
export const getRecentActivities = (limit = 20) => api.get('/activities/recent', { params: { limit } });
export const addActivity = (data) => api.post('/activities', data);

// Reminders API
export const getReminders = (type, id) => api.get(`/reminders/${type}/${id}`);
export const getUpcomingReminders = () => api.get('/reminders/upcoming');
export const createReminder = (data) => api.post('/reminders', data);
export const completeReminder = (id) => api.put(`/reminders/${id}/complete`);
export const deleteReminder = (id) => api.delete(`/reminders/${id}`);

// Deals API
export const getAllDeals = (search, status, deal_type, property_type) => {
  const params = {};
  if (search) params.search = search;
  if (status) params.status = status;
  if (deal_type) params.deal_type = deal_type;
  if (property_type) params.property_type = property_type;
  return api.get('/deals', { params });
};
export const getDeals = (type, id) => api.get(`/deals/${type}/${id}`);
export const createDeal = (data) => api.post('/deals', data);
export const updateDeal = (id, data) => api.put(`/deals/${id}`, data);
export const deleteDeal = (id) => api.delete(`/deals/${id}`);

// Audio Transcription API
export const transcribeAudio = (audioBase64, language = 'ar', mimeType = 'audio/webm') => {
  return api.post('/transcribe', { 
    audio: audioBase64,
    language: language,
    mimeType
  });
};

// Statistics API - add timestamp to prevent caching
export const getStatistics = () => api.get(`/statistics?t=${Date.now()}`);
export const getPriorityDashboard = () => api.get('/priorities/dashboard');

// Reports API
export const getDailyReport = () => api.get('/reports/daily');
export const getWeeklyReport = (language = 'ar') => api.get(`/reports/weekly?lang=${language}`);

// Export API
export const exportClients = () => {
  const token = localStorage.getItem('token');
  const url = `${API_BASE_URL}/clients/export`;
  // Create a temporary link with token in header - use fetch instead
  fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
    .then(response => response.blob())
    .then(blob => {
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = 'clients-export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    })
    .catch(err => console.error('Export failed:', err));
};

export const exportLeads = () => {
  const token = localStorage.getItem('token');
  const url = `${API_BASE_URL}/leads/export`;
  fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
    .then(response => response.blob())
    .then(blob => {
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = 'leads-export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    })
    .catch(err => console.error('Export failed:', err));
};

// Public (no auth)
export const getPublicRegistrationSettings = () => api.get('/public/registration-settings');

// Admin API (requires admin token in Authorization header)
export const adminLogin = (admin_token) => api.post('/admin/login', { admin_token });
export const getAdminRegistrationSettings = (adminToken) =>
  api.get('/admin/registration-settings', { headers: { Authorization: `Bearer ${adminToken}` } });
export const putAdminRegistrationSettings = (adminToken, data) =>
  api.put('/admin/registration-settings', data, { headers: { Authorization: `Bearer ${adminToken}` } });
export const getAdminUsers = (adminToken) => api.get('/admin/users', { headers: { Authorization: `Bearer ${adminToken}` } });
export const getAdminStats = (adminToken) => api.get('/admin/stats', { headers: { Authorization: `Bearer ${adminToken}` } });
export const getAdminWhatsAppOperations = (adminToken) =>
  api.get('/admin/whatsapp-operations', { headers: { Authorization: `Bearer ${adminToken}` } });
export const getUserStats = (adminToken, userId) => api.get(`/admin/users/${userId}/stats`, { headers: { Authorization: `Bearer ${adminToken}` } });
export const getUserData = (adminToken, userId) => api.get(`/admin/users/${userId}/data`, { headers: { Authorization: `Bearer ${adminToken}` } });
export const createUser = (adminToken, userData) => api.post('/admin/users', userData, { headers: { Authorization: `Bearer ${adminToken}` } });
/** Soft-deactivate platform user (sets deactivated_at; CRM/WhatsApp data retained). Same phone can be reactivated via POST /admin/users. */
export const deleteUser = (adminToken, userId) => api.delete(`/admin/users/${userId}`, { headers: { Authorization: `Bearer ${adminToken}` } });
export const getPendingUsers = (adminToken) => api.get('/admin/pending-users', { headers: { Authorization: `Bearer ${adminToken}` } });
export const approveUser = (adminToken, userId) => api.post(`/admin/users/${userId}/approve`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
export const rejectUser = (adminToken, userId) => api.post(`/admin/users/${userId}/reject`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
export const waitlistUser = (adminToken, userId) => api.post(`/admin/users/${userId}/waitlist`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });

// Google Sheets Trail Registrations API
export const getTrailRegistrations = (adminToken) => api.get('/admin/google-sheets/trail-registrations', { headers: { Authorization: `Bearer ${adminToken}` } });
export const importUserFromSheet = (adminToken, userData) => api.post('/admin/google-sheets/import-user', { userData }, { headers: { Authorization: `Bearer ${adminToken}` } });
export const bulkImportFromSheets = (adminToken, userDataList) => api.post('/admin/google-sheets/bulk-import', { userDataList }, { headers: { Authorization: `Bearer ${adminToken}` } });

// Account API
export const updateMyAccount = (data) => api.put('/users/me', data);

// WhatsApp Conversation Intelligence API
export const getWhatsAppConversations = (params = {}) => api.get('/whatsapp/conversations', { params });
export const getWhatsAppConversationsPage = (params = {}, offset = 0, limit = 60) =>
  api.get('/whatsapp/conversations', { params: { ...params, offset, limit } });
export const getConversationMessages = (conversationId, params = {}) => api.get(`/whatsapp/conversations/${conversationId}/messages`, { params });
export const getMessageByWamid = (wamid) => api.get(`/whatsapp/messages/by-wamid/${encodeURIComponent(wamid)}`);
export const markConversationRead = (conversationId) => api.post(`/whatsapp/conversations/${conversationId}/read`);
export const setConversationType = (conversationId, conversation_type) => api.put(`/whatsapp/conversations/${conversationId}/type`, { conversation_type });
export const resolveConversationFollowup = (conversationId) => api.post(`/whatsapp/conversations/${conversationId}/resolve-followup`);
export const createOrGetConversation = (phone, business_phone_number_id) =>
  api.post('/whatsapp/conversations', { phone, ...(business_phone_number_id ? { business_phone_number_id } : {}) });
export const createMessage = (conversationId, data) => api.post(`/whatsapp/conversations/${conversationId}/messages`, data);
export const getWhatsAppInsights = () => api.get('/whatsapp/insights');
export const linkConversationToClient = (conversationId, clientId) => api.post(`/whatsapp/conversations/${conversationId}/link-client`, { client_id: clientId });
export const autoLinkConversationClient = (conversationId) => api.post(`/whatsapp/conversations/${conversationId}/link-client`, { client_id: 'auto' });
export const createDealFromConversation = (conversationId, dealData) => api.post(`/whatsapp/conversations/${conversationId}/create-deal`, dealData);
export const matchUnitsToConversation = (conversationId) => api.get(`/whatsapp/conversations/${conversationId}/match-units`);
export const getWhatsAppAssignableUsers = () => api.get('/whatsapp/assignable-users');
export const getWhatsAppInboxAssignees = () => api.get('/whatsapp/inbox-assignees');
export const assignConversationToEmployee = (conversationId, employeeId, assignmentReason = '') =>
  api.put(`/whatsapp/conversations/${conversationId}/assign`, {
    employee_id: employeeId,
    assignment_reason: assignmentReason
  });
export const updateConversationTags = (conversationId, tags) => api.put(`/whatsapp/conversations/${conversationId}/tags`, { tags });
export const sendWhatsAppMessage = (conversationId, messageData) => {
  // Generate a per-attempt idempotency key to prevent duplicate sends on network retry
  const idempotencyKey = `${conversationId}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  return api.post('/whatsapp/send', { conversationId, ...messageData, idempotencyKey });
};
export const retryFailedWhatsAppMessage = (messageId) => api.post(`/whatsapp/messages/${messageId}/retry`);
export const testSendWhatsAppTemplate = (phone, template_name, phone_number_id) => api.post('/whatsapp/test-send', { phone, template_name, phone_number_id });
/**
 * @param {File|Blob} file
 * @param {{ purpose?: 'voice_note' }} [opts] - voice_note enables server-side MP4→AAC fix for browser voice recordings
 */
export const uploadWhatsAppMedia = (file, opts = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  if (opts.purpose) formData.append('purpose', opts.purpose);
  return api.post('/whatsapp/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getWhatsAppTemplates = () => api.get('/whatsapp/templates');
export const getMetaTemplates = () => api.get('/whatsapp/meta-templates');
export const getMetaTemplatesAll = () => api.get('/whatsapp/meta-templates-all');
export const getTemplateHeader = (templateName) => api.get(`/whatsapp/template-header/${encodeURIComponent(templateName)}`);
export const createMetaTemplate = (data) => api.post('/whatsapp/meta-templates', data);
// Template drafts (save for management review before submitting to Meta)
export const saveTemplateDraft = (data) => api.post('/whatsapp/template-drafts', data);
export const updateTemplateDraft = (id, data) => api.put(`/whatsapp/template-drafts/${id}`, data);
export const getTemplateDrafts = () => api.get('/whatsapp/template-drafts');
export const getTemplateDraft = (id) => api.get(`/whatsapp/template-drafts/${id}`);
export const submitTemplateDraft = (id) => api.post(`/whatsapp/template-drafts/${id}/submit`);
export const deleteTemplateDraft = (id) => api.delete(`/whatsapp/template-drafts/${id}`);
export const getWhatsAppSnippets = (category = null) => api.get('/whatsapp/snippets', { params: category ? { category } : {} });
export const createWhatsAppSnippet = (data) => api.post('/whatsapp/snippets', data);
export const updateWhatsAppSnippet = (id, data) => api.put(`/whatsapp/snippets/${id}`, data);
export const deleteWhatsAppSnippet = (id) => api.delete(`/whatsapp/snippets/${id}`);
export const recordSnippetUse = (id) => api.post(`/whatsapp/snippets/${id}/use`);

export const getWhatsAppInteractivePresets = () => api.get('/whatsapp/interactive-presets');
export const createWhatsAppInteractivePreset = (data) => api.post('/whatsapp/interactive-presets', data);
export const updateWhatsAppInteractivePreset = (id, data) => api.put(`/whatsapp/interactive-presets/${id}`, data);
export const deleteWhatsAppInteractivePreset = (id) => api.delete(`/whatsapp/interactive-presets/${id}`);
export const recordInteractivePresetUse = (id) => api.post(`/whatsapp/interactive-presets/${id}/use`);
export const getConversationActionSuggestions = (conversationId) => api.get(`/whatsapp/conversations/${conversationId}/action-suggestions`);
export const actOnActionSuggestion = (id, data) => api.post(`/whatsapp/action-suggestions/${id}/act`, data);
export const dismissActionSuggestion = (id) => api.post(`/whatsapp/action-suggestions/${id}/dismiss`);
export const getSmartSuggestionsEnabled = () => api.get('/whatsapp/settings/smart-suggestions');
export const setSmartSuggestionsEnabled = (enabled) => api.put('/whatsapp/settings/smart-suggestions', { enabled });
export const getAutoRemindersEnabled = () => api.get('/whatsapp/settings/auto-reminders');
export const setAutoRemindersEnabled = (enabled) => api.put('/whatsapp/settings/auto-reminders', { enabled });
export const getAutoAssignEnabled = () => api.get('/whatsapp/settings/auto-assign');
export const setAutoAssignEnabled = (enabled) => api.put('/whatsapp/settings/auto-assign', { enabled });
export const getTagSuggestions = () => api.get('/whatsapp/tags/suggestions');
export const generateQRCode = (phone, message) => api.get('/whatsapp/qr-code', { params: { phone, message } });
export const generateDeepLink = (phone, message) => api.get('/whatsapp/deep-link', { params: { phone, message } });
export const getWhatsAppTeamOverview = () => api.get('/whatsapp/team-overview');
export const getWhatsAppTeamPerformance = (period = 'all', dateFrom = '', dateTo = '', timeMode = 'business') => {
  const params = { period, time_mode: timeMode };
  if (period === 'custom' && dateFrom && dateTo) {
    params.date_from = dateFrom;
    params.date_to = dateTo;
  }
  return api.get('/whatsapp/team-performance', { params });
};
export const getWhatsAppTriageCounts = () => api.get('/whatsapp/triage-counts');
export const getWhatsAppSlaSettings = () => api.get('/whatsapp/sla/settings');
export const updateWhatsAppSlaSettings = (data) => api.put('/whatsapp/sla/settings', data);
export const getWhatsAppBusinessSettings = () => api.get('/whatsapp/business-settings');
export const updateWhatsAppBusinessSettings = (data) => api.put('/whatsapp/business-settings', data);
export const getWhatsAppIntegrations = (includeExpiration = false) => 
  api.get(`/whatsapp/integrations${includeExpiration ? '?include_expiration=true' : ''}`);
export const createWhatsAppIntegration = (data) => api.post('/whatsapp/integrations', data);
export const updateWhatsAppIntegration = (integrationId, data) => api.put(`/whatsapp/integrations/${integrationId}`, data);
export const deleteWhatsAppIntegration = (integrationId) => api.delete(`/whatsapp/integrations/${integrationId}`);
/** Owner or manager — returns full token; use sparingly; audit logged server-side */
export const revealWhatsAppIntegrationToken = (integrationId) =>
  api.post(`/whatsapp/integrations/${integrationId}/reveal-access-token`);

// Channel access management
export const getMyChannels = () => api.get('/channel-access/my-channels');
export const getUserChannelAccess = (userId) => api.get(`/channel-access/user/${userId}`);
export const updateUserChannelAccess = (userId, grants) => api.put(`/channel-access/user/${userId}`, { grants });
export const getDeptChannelDefaults = () => api.get('/channel-access/dept-defaults');
export const updateDeptChannelDefaults = (defaults) => api.put('/channel-access/dept-defaults', { defaults });

// Organization custom departments
export const getOrgDepartments = () => api.get('/organization/departments');
export const createOrgDepartment = (data) => api.post('/organization/departments', data);
export const updateOrgDepartment = (id, data) => api.put(`/organization/departments/${id}`, data);
export const deleteOrgDepartment = (id) => api.delete(`/organization/departments/${id}`);
export const getWhatsAppCampaignReport = () => api.get('/whatsapp/campaign-report');
export const runWhatsAppSlaEscalation = () => api.post('/whatsapp/sla/escalations/run');
export const getConversationNotes = (conversationId) => api.get(`/whatsapp/conversations/${conversationId}/notes`);
export const createConversationNote = (conversationId, note) => api.post(`/whatsapp/conversations/${conversationId}/notes`, { note });
export const getBroadcastCampaigns = () => api.get('/whatsapp/broadcasts');
export const createBroadcastCampaign = (data) => api.post('/whatsapp/broadcasts', data);
export const getBroadcastPreview = (targetFilter) => api.post('/whatsapp/broadcasts/preview', { target_filter: targetFilter });
export const getBroadcastPreviewList = (targetFilter, limit = 200) => api.post('/whatsapp/broadcasts/preview-list', { target_filter: targetFilter, limit });
export const runBroadcastCampaign = (campaignId) => api.post(`/whatsapp/broadcasts/${campaignId}/run`);
export const cancelBroadcastCampaign = (campaignId) => api.post(`/whatsapp/broadcasts/${campaignId}/cancel`);
export const updateBroadcastCampaign = (campaignId, data) => api.patch(`/whatsapp/broadcasts/${campaignId}`, data);
export const deleteBroadcastCampaign = (campaignId) => api.delete(`/whatsapp/broadcasts/${campaignId}`);
export const getBroadcastCampaignRecipients = (campaignId) => api.get(`/whatsapp/broadcasts/${campaignId}/recipients`);
/** Append-only delivery trace: API sends + Meta webhooks. Query: recipient_id, correlation_id, send_batch_id, limit, offset */
export const getBroadcastDeliveryEvents = (campaignId, params = {}) =>
  api.get(`/whatsapp/broadcasts/${campaignId}/delivery-events`, { params });
/** Human actions: create, update, run, retry, cancel, delete */
export const getBroadcastCampaignAuditLog = (campaignId, params = {}) =>
  api.get(`/whatsapp/broadcasts/${campaignId}/audit-log`, { params });
export const retryBroadcastRecipients = (campaignId, recipientIds) => api.post(`/whatsapp/broadcasts/${campaignId}/retry`, { recipient_ids: recipientIds });
export const setConversationBroadcastOptOut = (conversationId, optOut) => api.patch(`/whatsapp/conversations/${conversationId}/broadcast-opt-out`, { opt_out: optOut });

// Development utilities (dev mode only)
export const simulateConversation = (phone) => api.post('/dev/simulate-conversation', { phone });
export const clearConversations = () => api.delete('/dev/clear-conversations');

/** Saudi-biased place search (server proxies Nominatim + Photon); supports short codes e.g. RRRA6565 */
export const geocodeSearch = (q, lang) =>
  api.get('/geocode/search', { params: { q, lang: lang || 'ar' } }).then((r) => r.data);

// Client AI profile (RAG)
export const getClientAiProfile = (clientId) => api.get(`/clients/${clientId}/ai-profile`);
export const refreshClientAiProfile = (clientId) => api.post(`/clients/${clientId}/ai-profile/refresh`);
export const confirmClientAiProfile = (clientId) => api.post(`/clients/${clientId}/ai-profile/confirm`);
export const confirmClientAiContactRole = (clientId, contactRole) =>
  api.post(`/clients/${clientId}/ai-profile/contact-role`, { contact_role: contactRole });
export const backfillClientAiProfiles = (limit = 25) => api.post('/rag/backfill', { limit });
export const getClientAiContext = (clientId) => api.get(`/clients/${clientId}/ai-context`);

// Website intake (Option 1: their site → Real API)
export const listIntakeTokens = () => api.get('/intake/tokens');
export const createIntakeToken = (body) => api.post('/intake/tokens', body);
export const setIntakeTokenActive = (id, active) => api.patch(`/intake/tokens/${id}`, { active });
export const listInterestSubmissions = (params = {}) =>
  api.get('/intake/interest-submissions', { params });
export const listBrokerPartners = () => api.get('/intake/broker-partners');

// Events (P0 — QR / public registration + live ops)
export const listEvents = () => api.get('/events');
export const createEvent = (body) => api.post('/events', body);
export const updateEvent = (id, body) => api.patch(`/events/${id}`, body);
export const getEventLive = (id) => api.get(`/events/${id}/live`);
export const listEventRegistrations = (id, params = {}) =>
  api.get(`/events/${id}/registrations`, { params });
export const getEventFollowUp = (eventId, regId) =>
  api.get(`/events/${eventId}/registrations/${regId}/follow-up`);

// Organization branding
export const getOrganizationBranding = () => api.get('/organization/branding');
export const updateOrganizationBranding = (body) => api.put('/organization/branding', body);

// Floor blueprints (P1)
export const listFloorPlans = (propertyId) => api.get(`/properties/${propertyId}/floor-plans`);
export const createFloorPlan = (propertyId, body) =>
  api.post(`/properties/${propertyId}/floor-plans`, body);
export const updateFloorPlan = (id, body) => api.patch(`/floor-plans/${id}`, body);
export const deleteFloorPlan = (id) => api.delete(`/floor-plans/${id}`);

export default api;

